import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Upload, X, CheckCircle, SkipForward } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderDetails = location.state?.orderDetails;
  
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  if (!orderDetails) {
    navigate('/order');
    return null;
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      setPaymentScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentScreenshot) {
      toast.error('Please upload payment screenshot');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('order_id', orderDetails.id);
      formData.append('franchise_name', orderDetails.franchise_name);
      formData.append('total_amount', orderDetails.total_amount);
      formData.append('payment_screenshot', paymentScreenshot);

      await axios.post(`${API}/upload-payment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Payment screenshot uploaded successfully!');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      console.error('Error uploading payment:', error);
      toast.error('Failed to upload payment screenshot');
    } finally {
      setUploading(false);
    }
  };

  const handleSkipPayment = () => {
    toast.info('Payment skipped. Order has been placed.');
    navigate('/');
  };

  const removeScreenshot = () => {
    setPaymentScreenshot(null);
    setPreviewUrl(null);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <CheckCircle size={48} color="#16a34a" />
          <h2 style={styles.title}>Order Placed Successfully!</h2>
          <p style={styles.subtitle}>Order ID: {orderDetails.id.slice(0, 8)}</p>
        </div>

        <div style={styles.orderSummary}>
          <h3 style={styles.sectionTitle}>Order Summary</h3>
          <div style={styles.summaryRow}>
            <span>Franchise:</span>
            <strong>{orderDetails.franchise_name}</strong>
          </div>
          <div style={styles.summaryRow}>
            <span>Items:</span>
            <strong>{orderDetails.ordered_items.length} item(s)</strong>
          </div>
          <div style={styles.totalRow}>
            <span>Total Amount:</span>
            <strong>₹{orderDetails.total_amount}</strong>
          </div>
        </div>

        <div style={styles.paymentSection}>
          <h3 style={styles.sectionTitle}>Complete Payment</h3>
          <p style={styles.instruction}>Scan the QR code below to pay via PhonePe</p>

          {/* PhonePe QR Code */}
          <div style={styles.qrContainer}>
            <img
              src="/phonepe-qr.png"
              alt="PhonePe QR Code"
              style={styles.qrImage}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/300x300/6b4423/ffffff?text=PhonePe+QR+Code';
              }}
            />
            <p style={styles.qrLabel}>Scan to pay ₹{orderDetails.total_amount}</p>
          </div>

          {/* Upload Payment Screenshot */}
          <div style={styles.uploadSection}>
            <h4 style={styles.uploadTitle}>Upload Payment Screenshot</h4>
            
            {!previewUrl ? (
              <label style={styles.uploadArea} data-testid="upload-area">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={styles.fileInput}
                  data-testid="file-input"
                />
                <Upload size={32} color="#8b5e34" />
                <p style={styles.uploadText}>Click to upload screenshot</p>
                <p style={styles.uploadHint}>JPG, PNG (Max 5MB)</p>
              </label>
            ) : (
              <div style={styles.previewContainer}>
                <img src={previewUrl} alt="Payment Screenshot" style={styles.preview} />
                <button
                  onClick={removeScreenshot}
                  style={styles.removeBtn}
                  data-testid="remove-screenshot-btn"
                >
                  <X size={20} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={styles.actions}>
          <button
            onClick={handleSubmitPayment}
            disabled={!paymentScreenshot || uploading}
            className="btn-primary"
            style={{ 
              ...styles.submitBtn,
              opacity: !paymentScreenshot || uploading ? 0.5 : 1,
              cursor: !paymentScreenshot || uploading ? 'not-allowed' : 'pointer'
            }}
            data-testid="submit-payment-btn"
          >
            {uploading ? 'Uploading...' : 'Submit Payment'}
          </button>

          <button
            onClick={handleSkipPayment}
            className="btn-secondary"
            style={styles.skipBtn}
            data-testid="skip-payment-btn"
          >
            <SkipForward size={18} style={{ marginRight: '8px' }} />
            Skip Payment
          </button>
        </div>

        <p style={styles.note}>
          * You can skip payment now and pay later. Your order will be processed regardless.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    maxWidth: '600px',
    width: '100%',
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px',
    color: '#16a34a',
    margin: '16px 0 8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b5d54',
  },
  orderSummary: {
    background: '#f9f6f1',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    color: '#6b4423',
    marginBottom: '16px',
    fontWeight: '600',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    color: '#4a3728',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    marginTop: '8px',
    borderTop: '2px solid #e8dcc8',
    fontSize: '18px',
    color: '#6b4423',
  },
  paymentSection: {
    marginBottom: '24px',
  },
  instruction: {
    textAlign: 'center',
    color: '#6b5d54',
    marginBottom: '20px',
  },
  qrContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    background: '#f9f6f1',
    borderRadius: '12px',
    marginBottom: '24px',
  },
  qrImage: {
    width: '250px',
    height: '250px',
    objectFit: 'contain',
    border: '4px solid #6b4423',
    borderRadius: '12px',
    background: 'white',
  },
  qrLabel: {
    marginTop: '12px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#6b4423',
  },
  uploadSection: {
    marginTop: '24px',
  },
  uploadTitle: {
    fontSize: '16px',
    color: '#6b4423',
    marginBottom: '12px',
    fontWeight: '600',
  },
  uploadArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    border: '2px dashed #e8dcc8',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: '#f9f6f1',
  },
  fileInput: {
    display: 'none',
  },
  uploadText: {
    marginTop: '12px',
    fontSize: '16px',
    color: '#6b4423',
    fontWeight: '500',
  },
  uploadHint: {
    fontSize: '12px',
    color: '#8b5e34',
    marginTop: '4px',
  },
  previewContainer: {
    position: 'relative',
    display: 'inline-block',
    width: '100%',
  },
  preview: {
    width: '100%',
    maxHeight: '300px',
    objectFit: 'contain',
    borderRadius: '12px',
    border: '2px solid #e8dcc8',
  },
  removeBtn: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '24px',
  },
  submitBtn: {
    width: '100%',
  },
  skipBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  note: {
    marginTop: '16px',
    fontSize: '12px',
    color: '#8b5e34',
    textAlign: 'center',
    fontStyle: 'italic',
  },
};

export default PaymentConfirmation;
